import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgOtpInputComponent, NgOtpInputModule } from 'ng-otp-input';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs';
import { SecondaryOtpService } from 'src/app/services/secondary-otp.service';
import { selectUserDetails } from 'src/app/store/auth/auth.selector';
import { AutoUnsubscribeComponent } from 'src/app/utility/auto-unsubscribe.component';
import { showError } from 'src/app/utility/common-logic';
import { UserProfile } from 'src/app/utility/model/user-profile';
import { environment } from 'src/environments/environment';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NgOtpInputModule],
  selector: 'app-otp-modal',
  templateUrl: './otp-modal.component.html',
  styleUrls: ['./otp-modal.component.scss'],
})
export class OtpModalComponent
  extends AutoUnsubscribeComponent
  implements OnInit
{
  otp: string = '';
  message: string = '';
  verifyBtnDisabled: boolean = true;
  appName = environment?.settings?.app;
  counter = this.appName === 'payit' ? 120 : 180;
  timerInterval!: ReturnType<typeof setInterval>;
  otpExpired: boolean = false;
  isLoading: boolean = false;
  userDetails!: UserProfile | null;
  otpSettings = { length: 6, allowNumbersOnly: true };
  @ViewChild(NgOtpInputComponent, { static: false })
  ngOtpInput: NgOtpInputComponent;
  constructor(
    private otpService: SecondaryOtpService,
    private store: Store,
    private dialogRef: MatDialogRef<OtpModalComponent>,
    private router: Router,
    private translate: TranslateService,
    private toastr: ToastrService,
  ) {
    super();
  }

  ngOnInit() {
    this.requestSecondaryOtp();
    this.store
      .pipe(select(selectUserDetails), takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userDetails = user;
      });
  }

  onInputChange(value: string): void {
    if (!value) return;
    this.otp = value;
    this.verifyBtnDisabled = value.length !== this.otpSettings.length;
    this.message = '';
    if (
      value.length === this.otpSettings.length &&
      !this.isLoading &&
      !this.otpExpired
    ) {
      this.isLoading = true;
      this.verifyOtp();
    }
  }

  requestSecondaryOtp(): void {
    if (this.otpExpired) this.resetOtpValue();
    this.otpService
      .requestSecondaryOtp()
      .then(() => {
        this.otpExpired = false;
        this.message = '';
        this.resetTimer();
      })
      .catch((err: any) => {
        this.message = err?.error || err?.message;
        showError(this.message, this.toastr, this.translate, true);
      });
  }

  async verifyOtp(): Promise<void> {
    if (!this.userDetails?.pk) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this.otpService
      .verifyOtp(this.otp, this.userDetails.pk)
      .then((res: any) => {
        this.otpService.markOtpVerified(res.auth_token);
        this.dialogRef.close(true);
      })
      .catch(async (err: any) => {
        if (this.otpExpired) {
          this.message = this.translate.instant('msg.otpExpired');
          showError(this.message, this.toastr, this.translate, true);
          await this.router.navigateByUrl('/login');
          this.dialogRef.close(false);
          return;
        }

        this.message = err?.error || err?.message;
        showError(this.message, this.toastr, this.translate, true);
        this.resetOtpValue();
      })
      .finally(() => {
        this.isLoading = false;
        this.verifyBtnDisabled = true;
      });
  }

  resetOtpValue(): void {
    this.otp = '';
    this.ngOtpInput.setValue('');
  }

  resetTimer(): void {
    this.counter = this.appName === 'payit' ? 120 : 180;
    this.otpExpired = false;
    this.startTimer();
  }

  startTimer(): void {
    clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => {
      if (this.counter > 0) {
        this.counter--;
      } else {
        clearInterval(this.timerInterval);
        this.otpExpired = true;
        this.verifyBtnDisabled = true;
      }
    }, 1000);
  }

  override ngOnDestroy(): void {
    clearInterval(this.timerInterval);
    super.ngOnDestroy();
  }

  closeModal(): void {
    if (this.isLoading) {
      return;
    }

    this.otp = '';
    this.message = '';
    this.verifyBtnDisabled = true;

    clearInterval(this.timerInterval);
    this.dialogRef.close(false);
  }
}
